import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Shield, Loader2, ArrowLeft, Copy, Check } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/auth/mfa-setup")({
  head: () => ({ meta: [{ title: "Activar MFA — ZYNTRA" }] }),
  component: MfaSetupPage,
});

function MfaSetupPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [step, setStep] = useState<"loading" | "enroll" | "verify" | "done">("loading");
  const [factorId, setFactorId] = useState<string>("");
  const [qrSvg, setQrSvg] = useState<string>("");
  const [secret, setSecret] = useState<string>("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    (async () => {
      // Si ya tiene factor verificado, saltar a verify
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const verified = factors?.totp?.find((f) => f.status === "verified");
      if (verified) {
        navigate({ to: "/auth/mfa-verify" });
        return;
      }
      // Limpiar factores no verificados previos
      const unverified = factors?.totp?.filter((f) => f.status !== "verified") ?? [];
      for (const f of unverified) {
        await supabase.auth.mfa.unenroll({ factorId: f.id });
      }
      // Enroll nuevo
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        friendlyName: `ZYNTRA-${Date.now()}`,
      });
      if (error) {
        setError(error.message);
        return;
      }
      setFactorId(data.id);
      setQrSvg(data.totp.qr_code);
      setSecret(data.totp.secret);
      setStep("enroll");
    })();
  }, [user, authLoading, navigate]);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: code.trim(),
      });
      if (error) throw error;
      setStep("done");
      setTimeout(() => navigate({ to: "/" }), 1200);
    } catch (err: any) {
      setError(err.message || "Código incorrecto");
    } finally {
      setSubmitting(false);
    }
  }

  function copySecret() {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3 w-3" /> Volver
        </Link>
        <div className="rounded-2xl border border-border bg-card p-6">
          <div className="mb-4 flex items-center gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5">
              <Shield className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-black">Activar autenticación de 2 pasos</h1>
              <p className="text-xs text-muted-foreground">Obligatorio para admins y organizadores</p>
            </div>
          </div>

          {step === "loading" && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}

          {step === "enroll" && (
            <>
              <p className="mb-4 text-sm text-muted-foreground">
                Escaneá este código QR con Google Authenticator, Authy, 1Password o cualquier app TOTP.
              </p>
              <div className="mx-auto mb-3 flex h-52 w-52 items-center justify-center rounded-xl bg-white p-2">
                <img src={qrSvg} alt="Código QR para MFA" className="h-full w-full" />
              </div>
              <div className="mb-4 rounded-lg border border-border bg-muted/30 p-3">
                <p className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">
                  ¿No podés escanear? Ingresá esta clave:
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 break-all font-mono text-xs">{secret}</code>
                  <button
                    type="button"
                    onClick={copySecret}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                    aria-label="Copiar clave"
                  >
                    {copied ? <Check className="h-3.5 w-3.5 text-success" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              <form onSubmit={handleVerify} className="space-y-3">
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  required
                  autoFocus
                  className="w-full rounded-xl border border-border bg-input py-3 text-center font-mono text-xl tracking-[0.5em] text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {error && (
                  <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>
                )}
                <button
                  type="submit"
                  disabled={submitting || code.length !== 6}
                  className="w-full rounded-xl gradient-primary py-3 text-sm font-bold text-primary-foreground transition-all hover:opacity-90 disabled:opacity-50"
                >
                  {submitting ? "Verificando..." : "Activar MFA"}
                </button>
              </form>
            </>
          )}

          {step === "done" && (
            <div className="py-8 text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
                <Check className="h-6 w-6 text-success" />
              </div>
              <p className="text-sm font-semibold">MFA activado correctamente</p>
              <p className="mt-1 text-xs text-muted-foreground">Redirigiendo...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
